// @flow strict

import type {CustomFormField, CUSTOM_FIELDS, CustomForm} from 'src/types/crm';


type Operator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'includes'
  | 'startsWith'
  | 'endsWith'
  | 'isEmpty'
  | 'isNotEmpty';
type FormValues = {[key: string]: mixed};
type FormErrors = {[key: string]: string};
type TransformedField = {
  fieldId: number,
  value: string | null,
  fieldType?: string,
};

/**
 * Check if a string is a valid operator
 */
const isValidOperator = (op: string): boolean => {
  const validOperators: Array<Operator> = [
    'eq',
    'neq',
    'gt',
    'lt',
    'gte',
    'lte',
    'includes',
    'startsWith',
    'endsWith',
    'isEmpty',
    'isNotEmpty',
  ];
  return validOperators.includes((op: mixed));
};

/**
 * Safely get operator or fallback to 'eq'
 */
const getOperator = (op: string): Operator => {
  if (isValidOperator(op)) {
    // $FlowFixMe[incompatible-cast]
    return (op: Operator);
  }
  return 'eq';
};

/**
 * Checks if a condition is met for conditional fields
 */
export const checkCondition = (
  value: mixed,
  operator: Operator,
  expectedValue: mixed,
): boolean => {
  if (value === undefined || value === null) {
    return false;
  }

  switch (operator) {
    case 'eq':
      return value === expectedValue;
    case 'neq':
      return value !== expectedValue;
    case 'gt':
      return parseFloat(String(value)) > parseFloat(String(expectedValue));
    case 'lt':
      return parseFloat(String(value)) < parseFloat(String(expectedValue));
    case 'gte':
      return parseFloat(String(value)) >= parseFloat(String(expectedValue));
    case 'lte':
      return parseFloat(String(value)) <= parseFloat(String(expectedValue));
    case 'includes':
      return Array.isArray(value)
        ? value.includes(expectedValue)
        : String(value).includes(String(expectedValue));
    case 'startsWith':
      return String(value).startsWith(String(expectedValue));
    case 'endsWith':
      return String(value).endsWith(String(expectedValue));
    case 'isEmpty':
      return (
        value === '' ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      );
    case 'isNotEmpty':
      return (
        value !== '' &&
        value !== null &&
        value !== undefined &&
        (!Array.isArray(value) || value.length > 0)
      );
    default:
      return false;
  }
};

/**
 * Filter fields based on conditions
 */
export const getFieldsWithConditions = (
  values: FormValues,
  fields: Array<CustomFormField>,
  shouldCheckCondition?: boolean,
): Array<CustomFormField> => {
  return fields
    .filter((field) => field.show !== 'OFF')
    .filter((field) => {
      if (
        Array.isArray(field.condition) &&
        field.condition.length === 3 &&
        shouldCheckCondition
      ) {
        const conditionOperator = field.condition[1];
        const conditionValue = field.condition[2];
        if (conditionOperator !== undefined && conditionValue !== undefined) {
          return checkCondition(
            values[field.condition[0]],
            getOperator(String(conditionOperator)),
            conditionValue,
          );
        }
      }
      return true;
    });
};

/**
 * Create conditional set field value function
 */
export const makeConditional = (
  setFieldValue: (name: string, value: mixed) => void,
  fields: Array<CustomFormField>,
): ((name: string, value: mixed) => void) => {
  return (name: string, value: mixed): void => {
    setFieldValue(name, value);

    // Find all fields that depend on this field
    const dependentFields = fields.filter(
      (field) =>
        Array.isArray(field.condition) &&
        field.condition.length === 3 &&
        field.condition[0] === name,
    );

    // Handle dependent fields
    dependentFields.forEach((field) => {
      const dependentFieldName = `custom_${field.customFieldId}`;
      // Safely handle condition
      if (Array.isArray(field.condition) && field.condition.length === 3) {
        const conditionOperator = field.condition[1];
        const conditionValue = field.condition[2];

        if (conditionOperator !== undefined && conditionValue !== undefined) {
          const operator = getOperator(String(conditionOperator));
          const shouldShow = checkCondition(value, operator, conditionValue);

          if (!shouldShow) {
            setFieldValue(dependentFieldName, getInitialValue(field));

            // Recursively clear fields that depend on this one
            const nestedDependentFields = fields.filter(
              (f) =>
                Array.isArray(f.condition) &&
                f.condition.length === 3 &&
                f.condition[0] === dependentFieldName,
            );

            nestedDependentFields.forEach((nestedField) => {
              const nestedFieldName = `custom_${nestedField.customFieldId}`;
              setFieldValue(nestedFieldName, getInitialValue(nestedField));
            });
          }
        }
      }
    });
  };
};

/**
 * Generate initial value for a field based on its type
 */
export const getInitialValue = (field: CustomFormField): mixed => {
  // $FlowFixMe[unnecessary-optional-chain]
  // $FlowFixMe[prop-missing]
  const fieldType = field.customField?.fieldType || field?.fieldType || 'TEXT';

  switch (fieldType) {
    case 'BOOLEAN':
      return '';
    case 'DATE':
      return null;
    case 'NUMBER':
    case 'CURRENCY':
      return '';
    case 'SINGLE_SELECT':
    case 'TEXT':
      return '';
    case 'TEXT_AREA':
      return '';
    case 'SINGLE_USER_SELECT':
      return null;
    case 'MULTI_USER_SELECT':
      return [];
    case 'MULTI_SELECT':
      return [];
    case 'DATE_RANGE':
    case 'NUMBER_RANGE':
    case 'CURRENCY_RANGE':
      return [null, null];
    case 'SINGLE_ATTACHMENT':
      return null;
    case 'MULTIPLE_ATTACHMENT':
      return [];
    default:
      return '';
  }
};

/**
 * Generate initial values for all fields in the form
 */
export const generateInitialValues = (
  fields: Array<CustomFormField>,
  initialValues: FormValues = {},
): FormValues => {
  const values: FormValues = {...initialValues};
  fields.forEach((field) => {
    const fieldName = `custom_${field.customFieldId}`;
    if (values[fieldName] === undefined) {
      values[fieldName] = getInitialValue(field);
    }
  });

  return values;
};

/**
 * Computes initial values for all fields in the form
 */
export const computeInitialValues = (
  fieldType: string,
  field: CUSTOM_FIELDS,
): mixed => {
  const initialValue = field.value;
  console.log(initialValue, 'initialValue', fieldType);
  switch (fieldType) {
    case 'BOOLEAN':
      return initialValue ?? '';
    case 'DATE':
      return initialValue ? initialValue : null;
    case 'NUMBER':
    case 'CURRENCY':
      return initialValue ? Number(initialValue) : '';
    case 'SINGLE_SELECT':
    case 'TEXT':
      return initialValue ?? '';
    case 'TEXT_AREA':
      return initialValue ?? '';
    case 'SINGLE_USER_SELECT':
      return (
        // $FlowFixMe[prop-missing] - users property exists at runtime but is missing in the type
        field.users?.map((user) => {
          return {
            key: user.id,
            label: user.prettyName,
          };
        }) ?? null
      );
    case 'MULTI_USER_SELECT':
      return (
        // $FlowFixMe[prop-missing] - users property exists at runtime but is missing in the type
        field.users?.map((user) => {
          return {
            key: user.id,
            label: user.prettyName,
          };
        }) ?? []
      );
    case 'MULTI_SELECT':
      return (initialValue ?? '').split(',').map((value) => value.trim()) ?? [];
    case 'DATE_RANGE':
      const [start, end] = (initialValue ?? '').split(',');
      return [
        start ? new Date(Number(start) * 1000) : null,
        end ? new Date(Number(end) * 1000) : null,
      ];
    case 'NUMBER_RANGE':
    case 'CURRENCY_RANGE':
      const [min, max] = (initialValue ?? '').split(',');
      return [min ? Number(min) : null, max ? Number(max) : null];
    case 'SINGLE_ATTACHMENT':
      return initialValue ?? null;
    case 'MULTIPLE_ATTACHMENT':
      return (initialValue ?? '').split(',').map((value) => value.trim()) ?? [];
    default:
      return initialValue ?? '';
  }
};

/**
 * Format field value for submission based on field type
 */
export const formatFieldValue = (value: mixed, fieldType: string): mixed => {
  if (value === null || value === undefined) {
    return null;
  }

  switch (fieldType) {
    case 'BOOLEAN':
      return value === 'Yes' || value === true;
    case 'DATE':
      return value ? Number(value) : null;
    case 'NUMBER':
      return value === '' ? null : Number(value);
    case 'CURRENCY':
      return value === '' ? null : Number(value);
    case 'MULTI_SELECT':
      return Array.isArray(value) ? value : [];
    case 'DATE_RANGE':
    case 'NUMBER_RANGE':
    case 'CURRENCY_RANGE':
      if (Array.isArray(value)) {
        return [
          value[0] === '' || value[0] === null ? null : Number(value[0]),
          value[1] === '' || value[1] === null ? null : Number(value[1]),
        ];
      }
      return [null, null];
    case 'SINGLE_ATTACHMENT':
      return value;
    case 'MULTIPLE_ATTACHMENT':
      return Array.isArray(value) ? value : [];
    case 'MULTI_USER_SELECT':
      return Array.isArray(value) ? value : [];
    default:
      return value;
  }
};

/**
 * Formats all form values for submission
 */
export const formatSubmitValues = (
  values: FormValues,
  fields: Array<CustomFormField>,
): {[string]: mixed} => {
  const formattedValues: {[string]: mixed} = {};

  // Format values based on field types
  fields.forEach((field) => {
    const fieldName = `custom_${field.customFieldId}`;
    if (values[fieldName] !== undefined) {
      formattedValues[fieldName] = formatFieldValue(
        values[fieldName],
        field.customField.fieldType,
      );
    }
  });

  return formattedValues;
};

/**
 * Validates an email address
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates if a value is empty
 */
const isEmpty = (value: mixed): boolean => {
  if (Array.isArray(value)) {
    return value.length === 0 || value.every((v) => isEmpty(v));
  }
  return value === undefined || value === null || String(value).trim() === '';
};

/**
 * Generate form validation function
 */
export const generateValidator = (
  fields: Array<CustomFormField>,
): ((values: FormValues) => FormErrors) => {
  return (values: FormValues): FormErrors => {
    const errors: FormErrors = {};

    // Get fields that should be visible based on conditions
    const visibleFields = getFieldsWithConditions(values, fields, true);

    // Check required fields
    visibleFields
      .filter((field) => field.show === 'MANDATORY')
      .forEach((field) => {
        const fieldName = `custom_${field.customFieldId}`;
        const value = values[fieldName];

        if (isEmpty(value)) {
          errors[fieldName] = 'Required';
        }
      });

    // Validate field types and special validations
    visibleFields.forEach((field) => {
      const fieldName = `custom_${field.customFieldId}`;
      const value = values[fieldName];
      const fieldType = field.customField.fieldType;

      // Skip if already has error or no value
      if (errors[fieldName] || isEmpty(value)) {
        return;
      }

      // Email validation
      if (field.validation && field.validation.type === 'EMAIL') {
        if (!isValidEmail(String(value))) {
          errors[fieldName] = 'Please enter a valid email address';
        }
      }

      // Number validation
      if (
        (fieldType === 'NUMBER' || fieldType === 'CURRENCY') &&
        isNaN(Number(value))
      ) {
        errors[fieldName] = 'Please enter a valid number';
      }

      // Range validations
      if (fieldType.includes('_RANGE') && Array.isArray(value)) {
        const [start, end] = value;
        if (start != null && end != null) {
          const startNum = Number(start);
          const endNum = Number(end);

          if (!isNaN(startNum) && !isNaN(endNum) && startNum > endNum) {
            errors[fieldName] = `Minimum can't be greater than maximum`;
          }
        }
      }

      // Custom validation for specific field types
      if (field.validation && field.validation.value) {
        const {absoluteValue} = field.validation.value;

        if (absoluteValue !== undefined && fieldType === 'NUMBER') {
          const numValue = Number(value);
          if (!isNaN(numValue) && numValue < absoluteValue) {
            errors[fieldName] = `Value must be at least ${absoluteValue}`;
          }
        }
      }
    });

    return errors;
  };
};

/**
 * Prepares form values for submission to API
 */
export const prepareSubmitValues = (
  values: FormValues,
  fields: Array<CustomFormField>,
): {
  [string]: mixed,
  customFieldValues: Array<{fieldId: number, value: mixed}>,
} => {
  const standardValues: {[string]: mixed} = {};
  const customFieldValues: Array<{fieldId: number, value: mixed}> = [];

  // Process all values
  Object.keys(values).forEach((key) => {
    if (key.startsWith('custom_')) {
      const fieldId = parseInt(key.split('_')[1], 10);
      const field = fields.find((f) => f.customFieldId === fieldId);

      if (field) {
        const formattedValue = formatFieldValue(
          values[key],
          field.customField.fieldType,
        );

        customFieldValues.push({
          fieldId,
          value: formattedValue,
        });
      }
    } else {
      standardValues[key] = values[key];
    }
  });

  return {
    ...standardValues,
    customFieldValues,
  };
};

export const transformCustomFields = (
  input: FormValues,
  fields?: Array<CustomFormField>,
): Array<TransformedField> => {
  const fieldMap = {};

  // Create a mapping of field IDs to field types if fields are provided
  if (fields) {
    fields.forEach((field) => {
      fieldMap[field.customFieldId] = field.customField.fieldType;
    });
  }

  return Object.entries(input)
    .map(([key, value]: [string, mixed]): TransformedField => {
      if (!key.startsWith('custom_')) {
        return {fieldId: 0, value: null};
      }

      const fieldId = parseInt(key.replace('custom_', ''), 10);
      let transformedValue: string | null = null;

      if (Array.isArray(value)) {
        // Handle arrays
        if (value.length === 0 || (value.length === 1 && value[0] === '')) {
          transformedValue = null;
        }

        // else if (
        //   value[0] &&
        //   typeof value[0] === 'object' &&
        //   value[0].key !== undefined
        // ) {
        //   // SINGLE_USER_SELECT
        //   transformedValue = String(value[0].key);
        // }
        // After testing, we will remove above code and use below code
        else if (
          Array.isArray(value) &&
          value[0] &&
          typeof value[0] === 'object' &&
          value[0].key !== undefined
        ) {
          // SINGLE_USER_SELECT
          transformedValue = String(
            value
              // $FlowFixMe[incompatible-use]
              .map((v) => v && v.key)
              .filter(Boolean)
              .join(','),
          );
        } else if (value[0] instanceof Date || key === 'custom_138') {
          // DATE_RANGE
          transformedValue = value
            .map((date) => {
              if (date instanceof Date) {
                return Math.floor(date.getTime() / 1000);
              }
              // Fallback handling for non-Date objects
              return Math.floor(new Date(String(date)).getTime() / 1000);
            })
            .join(',');
        } else {
          transformedValue = value.map(String).join(',');
        }
      } else if (typeof value === 'string') {
        transformedValue = value.trim() === '' ? null : value;
      } else if (value instanceof Date || key === 'custom_11') {
        // DATE
        transformedValue = Math.floor(
          value instanceof Date
            ? value.getTime() / 1000
            : new Date(String(value)).getTime() / 1000,
        ).toString();
      } else if (typeof value === 'number') {
        transformedValue = value.toString();
      }

      return {
        fieldId,
        value: transformedValue,
        fieldType: fieldMap[fieldId],
      };
    })
    .filter((field) => field.fieldId !== 0);
};

export const convertCustomFieldsListToForm = (
  customFieldsList: Array<CustomFormField>,
): Array<CustomForm> => {
  return [
    // $FlowFixMe[prop-missing]
    {
      name: 'Job Form',
      formOf: 'JOB',
      sections: [],
      // $FlowFixMe[incompatible-return]
      fields: customFieldsList.map((field) => ({
        questionTag: field.questionTag,
        show: 'MANDATORY',
        causeVersionChange: false,
        // $FlowFixMe[prop-missing]
        customFieldId: field.id,
        parentFieldId: 0,
        offerLetterFieldId: 0,
        canApproverView: false,
        canAgencyView: false,
        canReferralView: false,
        canInternalJobPortalView: true,
        customField: {
          ...field,
          // $FlowFixMe[prop-missing]
          customFieldId: field.id,
        },
        locked: false,
        editable: true,
      })),
      active: true,
      filteredCustomFieldIds: [0],
    },
  ];
};
